import * as React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

import { selectDigitalMap } from 'ducks/universal/digitalMap';
import { useItineraries } from '../useItineraries';

import styles from './SideModalItineraryList.module.css';
import { Itinerary } from 'models/itinerary';
import { AnimatedButton } from '../AnimatedButton';
import { ItineraryCard } from '../Itineraries/ItineraryCard';
import { DigitalMap } from 'models/digitalMap';

const getItineraryDateRange = (itinerary: Itinerary) => {
  if (itinerary.date_itineraries.length === 1) {
    return moment(itinerary.date_itineraries[0].date).locale('en').format('MMM D');
  }
  return `${moment(itinerary.date_itineraries[0].date).locale('en').format('MMM D')} - ${moment(
    itinerary.date_itineraries[itinerary.date_itineraries.length - 1].date
  )
    .locale('en')
    .format('MMM D')}`;
};

const getItineraryThumbnail = (itinerary: Itinerary, digitalMap: DigitalMap | null) => {
  if (!digitalMap) return '';

  for (const date of itinerary.date_itineraries) {
    for (const item of date.items) {
      const pin = digitalMap?.pins?.find((pin) => pin.key === item.pin_key);
      const thumbnail = pin?.media_items?.find((item) => item.type === 'IMAGE')?.url;
      if (thumbnail) {
        return thumbnail;
      }
    }
  }
  return '';
};

interface SideModalItineraryListProps {
  onEnlargeClick: () => void;
  onCreateItineraryClick: () => void;
  onItineraryClick: (itinerary: Itinerary) => void;
}

export const SideModalItineraryList = ({
  onEnlargeClick,
  onCreateItineraryClick,
  onItineraryClick,
}: SideModalItineraryListProps) => {
  const { t } = useTranslation();
  const { itineraries } = useItineraries();
  const digitalMap = useSelector(selectDigitalMap);

  const recommendedItineraries = digitalMap?.recommended_itineraries ?? [];

  // Sort itineraries by first date
  const sortedItineraries = [...itineraries].sort((a, b) => {
    if (!a.date_itineraries.length) return 1;
    if (!b.date_itineraries.length) return -1;

    const dateA = moment(a.date_itineraries[0].date);
    const dateB = moment(b.date_itineraries[0].date);
    return dateA.valueOf() - dateB.valueOf();
  });

  return (
    <div className={styles.pageContent}>
      <div className={styles.headerSection}>
        <img
          onClick={onEnlargeClick}
          src="/static/images/map/enlarge.svg"
          className={styles.enlargeIcon}
        />
        <div className={styles.textContent}>
          <h1 className={styles.title}>{t('Itinerary Maker')}</h1>
          <p className={styles.subtitle}>{t('Plan Your Day From Here!')}</p>
        </div>
      </div>

      <AnimatedButton
        clickedClassName={styles.clicked}
        className={styles.createButton}
        onClick={onCreateItineraryClick}
      >
        <span className={styles.plusIcon}>+</span>
        {t('Create New Itinerary')}
      </AnimatedButton>

      {sortedItineraries.length > 0 && (
        <>
          <h2 className={styles.sectionTitle}>{t('My Itineraries')}</h2>
          <div className={styles.itineraryList}>
            {sortedItineraries.map((itinerary, index) => (
              <ItineraryCard
                key={index}
                onClick={() => onItineraryClick(itinerary)}
                itineraryId={itinerary.id}
                image={getItineraryThumbnail(itinerary, digitalMap)}
                title={itinerary.title}
                date={getItineraryDateRange(itinerary)}
              />
            ))}
          </div>
        </>
      )}

      {recommendedItineraries.length > 0 && (
        <>
          <h2 className={styles.sectionTitle}>{t('Recommended Itineraries')}</h2>
          <div className={styles.itineraryList}>
            {recommendedItineraries.map((itinerary, index) => (
              <ItineraryCard
                key={index}
                onClick={() => onItineraryClick(itinerary)}
                itineraryId={itinerary.id}
                image={getItineraryThumbnail(itinerary, digitalMap)}
                title={itinerary.title}
                date={getItineraryDateRange(itinerary)}
                isRecommended
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
